import { useElectronAPI } from "@circle-react/hooks/utils/useElectronAPI";
import type { ReactNode } from "react";

import type { Commentable } from "@/react/components/CommentsCompass/types";
import { usePunditUserContext } from "@/react/contexts/punditUserContext";
import { useIsEmbed } from "@/react/hooks/useIsEmbed";
import type { Comment } from "@/react/types/Comment";

import { CommentsConfigProvider } from "./ConfigContext";
import { CommentsScrollProvider } from "./ScrollContext";
import { CommentsSectionProvider } from "./SectionContext";

export interface CommentsProviderProps {
  children: ReactNode;
  commentable: Commentable;
  onCommentCreated?: (comment: Comment) => void;
  onCommentDeleted?: (comment: Comment) => void;
}

const noop = () => {};

export const Provider = ({
  children,
  commentable,
  onCommentCreated = noop,
  onCommentDeleted = noop,
}: CommentsProviderProps) => {
  const {
    id: postId,
    spaceId,
    isCommentsClosed,
    isCommentsEnabled,
    isLikingEnabled,
    path,
  } = commentable;

  const { currentCommunityMember, isViewOnlyMasquerading } =
    usePunditUserContext();
  const { isElectron } = useElectronAPI();
  const isEmbedded = useIsEmbed();

  const isReplyingEnabled = isCommentsEnabled && !isCommentsClosed;
  const canCompose =
    !!currentCommunityMember && isReplyingEnabled && !isViewOnlyMasquerading;

  return (
    <CommentsSectionProvider>
      <CommentsConfigProvider
        postId={postId}
        spaceId={spaceId}
        path={path}
        canCompose={canCompose}
        isEmbedded={isEmbedded}
        isReadOnly={isViewOnlyMasquerading}
        isCommentsClosed={isCommentsClosed}
        isCommentsEnabled={isCommentsEnabled}
        isLikingEnabled={isLikingEnabled}
        isReplyingEnabled={isReplyingEnabled}
        onCommentCreated={onCommentCreated}
        onCommentDeleted={onCommentDeleted}
      >
        <CommentsScrollProvider isAutoScrollEnabled={!isElectron}>
          {children}
        </CommentsScrollProvider>
      </CommentsConfigProvider>
    </CommentsSectionProvider>
  );
};
